import {Simulation} from "./Simulation.js";
import {WebGL} from "./WebGL.js";
import debounce from 'lodash.debounce';
import { gsap } from "gsap";

export default class FeaturesCanvas {
    constructor(canvas) {
        this.canvas = canvas;
        this.parent = this.canvas.closest('[data-features]');

        this.NUM_METABALLS = 12;
        this.canvasMultiplier = 1.3;

        this.breakpoint = window.matchMedia('(min-width:992px)');
        this.breakpoint.addEventListener('change', () => {
            if(this.breakpoint.matches) {
                this.init();
            } else {
                this.destroy();
            }
        });

        if(this.breakpoint.matches) {
            this.init();
        } else {
            this.destroy();
        }
    }

    init() {
        this.handleResize();

        this.container = this.canvas.closest('[data-barba="container"]');
        this.simulation = new Simulation(this);
        this.step();

        this.boundResizeListener = debounce(() => {
            this.handleResize();
        }, 100);
		window.addEventListener(
			'resize',
			this.boundResizeListener
		);
    }

    handleResize() {
        this.canvas.remove();

        this.canvas = document.createElement('canvas');
        this.canvas.classList.add('t-features__canvas');
        this.canvas.setAttribute('id', 'canvas');
        if(this.ballsInPosition) {
            gsap.set(this.canvas, {
                autoAlpha: 0.3
            });
        }
        this.parent.appendChild(this.canvas);

		const parentRect = this.parent.getBoundingClientRect();

        this.WIDTH = parentRect.width*this.canvasMultiplier;
        this.HEIGHT = parentRect.height*this.canvasMultiplier;

        this.canvas.width = this.WIDTH;
        this.canvas.height = this.HEIGHT;

        this.gl = this.canvas.getContext('webgl2');
        this.webGl = new WebGL(this.gl);

        const program = this.compileProgram();

        this.setupGeometry(program);
        this.metaballsHandle = this.webGl.getUniformLocation(program, 'metaballs');
        this.heightMapHandle = this.webGl.getUniformLocation(program, 'heightMap');

        if(this.simulation) {
            this.simulation.handleResize();
            this.simulation.positionBalls();
        }
	}

    positionBalls() {
        if(this.simulation) {
            this.simulation.positionBalls();
        }
        this.ballsInPosition = true;
    }

    step(){
        this.simulation.step();
        this.render();

        this.raf = requestAnimationFrame(() => this.step());
    }

    render() {

        // To send the data to the GPU, we first need to
        // flatten our data into a single array.
        const dataToSendToGPU = new Float32Array(4 * this.NUM_METABALLS);

        this.gl.clearColor(0.0, 0.5, 0.0, 1.0);
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);

        for (var i = 0; i < this.NUM_METABALLS; i++) {
            var baseIndex = 4 * i;
            const metaball = this.simulation.metaballs[i];
            dataToSendToGPU[baseIndex + 0] = metaball.x;
            dataToSendToGPU[baseIndex + 1] = metaball.y;
            dataToSendToGPU[baseIndex + 2] = metaball.r;
            dataToSendToGPU[baseIndex + 3] = metaball.angle;
        }
        this.gl.uniform4fv(this.metaballsHandle, dataToSendToGPU);

        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
    }

    compileProgram() {
        const gl = this.gl;
        const webGl = this.webGl;

        var vertexShader = this.webGl.compileShader(`#version 300 es
precision mediump float;
in vec2 position;

void main() {
    // position specifies only x and y.
    // We set z to be 0.0, and w to be 1.0
    gl_Position = vec4(position, 0.0, 1.0);
}
`, this.gl.VERTEX_SHADER);

        var fragmentShader = this.webGl.compileShader(`#version 300 es
precision mediump float;
uniform vec4 metaballs[${this.NUM_METABALLS}];
layout(location = 0) out vec4 fragColor;




float isoline(float val, float lg, float ref, float pas, float tickness) {
    float v = abs(mod(val-ref+pas*.5, pas)-pas*.5)/lg - .1*tickness;
    return smoothstep(.1, .7, v);
}

float map(float value, float min1, float max1, float min2, float max2) {
    // Convert the current value to a percentage
    // 0% - min1, 100% - max1
    float perc = (value - min1) / (max1 - min1);

    // Do the same operation backwards with min2 and max2
    return perc * (max2 - min2) + min2;
}


void main(){
    float x = gl_FragCoord.x;
    float y = gl_FragCoord.y;
    float v = 0.0;
    for (int i = 0; i < ${this.NUM_METABALLS}; i++) {
        vec4 mb = metaballs[i];
        float dx = mb.x - x;
        float dy = mb.y - y;
        float r = mb.z;
        float angle = mb.w;
        float factor = 2.0;
        float distance = sqrt(dx*dx + dy*dy);
        float power = clamp(1./(distance * angle), 0., 1.);
        power = pow(power, 0.4);
        power *= r;
        v += power;
    }

    v = clamp(v, 0., 25.1);

    float lg = 2.*length(vec2(dFdx(v), dFdy(v)));

    float k1 = isoline(v, lg, 0., 0.5, 0.9);

    float opacityScore = map(v , 18. , 25.1, 0. , 1. );

    // paletize value
    vec3 col = vec3(1);

    // apply isoline to color
    col *= k1;

    fragColor = vec4(1.0-col,1);
    vec3 background = vec3(35.0/256.0, 34.0/256.0,34.0/256.0);
    vec3 limit = max(background, vec3(opacityScore));

    fragColor = clamp(fragColor, vec4(background, 0), vec4(limit,1));
}
`, this.gl.FRAGMENT_SHADER);

        var program = this.gl.createProgram();
        this.gl.attachShader(program, vertexShader);
        this.gl.attachShader(program, fragmentShader);
        this.gl.linkProgram(program);
        this.gl.useProgram(program);

        return program;
    }


    setupGeometry(program){
        const gl = this.gl;
        const webGl = this.webGl;

        // Set up 4 vertices, which we'll draw as a rectangle
        // via 2 triangles
        //
        //   A---C
        //   |  /|
        //   | / |
        //   |/  |
        //   B---D
        //
        // We order them like so, so that when we draw with
        // gl.TRIANGLE_STRIP, we draw triangle ABC and BCD.
        var vertexData = new Float32Array([
            -1.0,  1.0, // top left
            -1.0, -1.0, // bottom left
            1.0,  1.0, // top right
            1.0, -1.0, // bottom right
        ]);
        var vertexDataBuffer = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, vertexDataBuffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, vertexData, this.gl.STATIC_DRAW);

        /**
         * Attribute setup
         */

        // To make the geometry information available in the shader as attributes, we
        // need to tell WebGL what the layout of our data in the vertex buffer is.
        var positionHandle = this.webGl.getAttribLocation(program, 'position');
        this.gl.enableVertexAttribArray(positionHandle);
        this.gl.vertexAttribPointer(positionHandle,
            2, // position is a vec2
            this.gl.FLOAT, // each component is a float
            this.gl.FALSE, // don't normalize values
            2 * 4, // two 4 byte float components per vertex
            0 // offset into each span of vertex data
        );

    }

    destroy() {
        if(this.raf) {
            cancelAnimationFrame(this.red);
        }
        if(this.simulation) {
            this.simulation.destroy();
        }

        window.removeEventListener(
			'resize',
			this.boundResizeListener
		);
    }
}
